import { getPacsAETitle, getDicomWebProxyURL, getAppAuthToken } from '../../constant';
import { calculateAge } from '../../Utils/utils';
import { DicomMetadataStore } from '@ohif/core';
import formatPN from '../../../../platform/core/src/utils/formatPN';
import formatContentItemValue from '../../../../extensions/cornerstone-dicom-sr/src/utils/formatContentItem';
import { measurementAliases, resolveFindingSite } from './measurementAliases';
import { normalizeUnitSuperscriptsDisplay } from '../../Utils/unitSuperscriptDisplay';
import { bsa as echoBsa } from '../../Utils/echoCalc';

export const fetchPatientDetails = async (studyInstanceUID: string) => {
  try {
    const base = getDicomWebProxyURL();
    const aet = getPacsAETitle();
    const patientStudyDetails = await fetch(
      `${base}/rs/studies/${studyInstanceUID}/series?limit=21&includefield=all&offset=0&orderby=SeriesNumber&ReceivingApplicationEntityTitleOfSeries=${aet}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAppAuthToken()}`,
        },
      }
    );

    if (!patientStudyDetails.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await patientStudyDetails.json();

    return data;
  } catch (error) {
    console.log(error);
  }
  return null;
};

export const extractSeriesInstanceUID = data => {
  if (!data) {
    return null;
  }

  const result = data?.filter(item => item['00080060']?.Value?.[0] === 'SR');
  return result.length > 0 ? result[result.length - 1]['0020000E']?.Value?.[0] : null; // Use latest SR (last by SeriesNumber)
};

export const extractPatientDemographics = (data, bsaFromSR = null) => {
  if (!data) {
    return {};
  }

  const patientName = data?.map(item => item['00100010']?.Value?.[0])[0]?.Alphabetic || '';
  const patientGender = data?.map(item => item['00100040']?.Value?.[0])[0] || '';
  const patientID = data?.map(item => item['00100020']?.Value?.[0])[0] || '';
  const patientDOB = data?.map(item => item['00100030']?.Value?.[0])[0] || '';
  const formattedDOB = patientDOB
    ? `${patientDOB.substring(6, 8)}/${patientDOB.substring(4, 6)}/${patientDOB.substring(0, 4)}`
    : '';
  const patientAge = patientDOB ? calculateAge(patientDOB) : '';
  const SNNumber = data?.map(item => item['00080050']?.Value?.[0])[0] || '';
  const NumberOfImages = data?.map(item => item['00201208']?.Value?.[0])[0] || '';
  const weightRaw = data?.map(item => item['00101030']?.Value?.[0])[0] || '';
  const weight = weightRaw ? `${parseFloat(weightRaw).toFixed(1)} kg` : '';
  const heightRaw = data?.map(item => item['00101020']?.Value?.[0])[0] || '';
  const height = heightRaw ? `${parseFloat(heightRaw)} m` : '';

  const studyDate = data?.map(item => item['00080020']?.Value?.[0])[0] || 'N/A';
  const modality = data?.map(item => item['00080060']?.Value?.[0])[0] || 'N/A';
  const studyTime = data?.map(item => item['00080030']?.Value?.[0])[0] || 'N/A';

  // Calculate BSA if weight and height are available
  const calculateBSA = (height, weight) => {
    const heightInCm = height * 100;
    return Math.sqrt((heightInCm * weight) / 3600).toFixed(1);
  };
  const bsa =
    bsaFromSR !== null && bsaFromSR !== 'N/A'
      ? `${parseFloat(bsaFromSR).toFixed(1)} m²`
      : weightRaw && heightRaw
        ? `${calculateBSA(heightRaw, weightRaw)} m²`
        : '';
  const referringPhysicianName =
    data?.map(item => item['00080090']?.Value?.[0])[0]?.Alphabetic || '';
  const gender = patientGender === 'M' ? 'Male' : patientGender === 'F' ? 'Female' : 'Other';

  /**
   * The report's {{date}} field is the STUDY's date/time (DICOM StudyDate/StudyTime,
   * tags 00080020/00080030 — already extracted above as studyDate/studyTime, but until
   * now never used). It was `formatDate(new Date())` — today, at whatever moment the
   * report happened to be opened — so every report opened the same day showed the exact
   * same value regardless of which patient/study it was, and never carried a time at all.
   */
  const formatStudyDateTime = (dicomDate, dicomTime) => {
    if (!dicomDate || dicomDate.length !== 8) {
      return formatDate(new Date());
    }
    const day = dicomDate.substring(6, 8);
    const month = dicomDate.substring(4, 6);
    const year = dicomDate.substring(0, 4);
    let time = '';
    if (dicomTime && dicomTime !== 'N/A' && dicomTime.length >= 4) {
      const hh = dicomTime.substring(0, 2);
      const mm = dicomTime.substring(2, 4);
      const ss = dicomTime.length >= 6 ? dicomTime.substring(4, 6) : '';
      time = ` ${hh}:${mm}${ss ? `:${ss}` : ''}`;
    }
    return `${day}/${month}/${year}${time}`;
  };
  const currentDate = formatStudyDateTime(studyDate, studyTime);
  const fullName = formatPN(patientName);
  const [lastName, firstName] = fullName.split(',').map(part =>
    part
      .trim()
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase())
  );

  return {
    patientName: fullName,
    firstName,
    lastName,
    gender,
    patientID,
    weight,
    height,
    bsa,
    heightRawM: heightRaw ? parseFloat(String(heightRaw)) : null,
    weightRawKg: weightRaw ? parseFloat(String(weightRaw)) : null,
    sex: gender,
    date: currentDate,
    referred_by: referringPhysicianName,
    dob: formattedDOB,
    age: patientAge,
    snNumber: SNNumber,
    numberOfImages: NumberOfImages,
    studyDate: studyDate,
    studyTime: studyTime,
    modality,
    report_status: 'Draft',
  };
};

export function extractContentItemData(contentItem, nodeIndexesTree, continuityOfContent) {
  if (!contentItem) {
    return {};
  }
  const { ConceptNameCodeSequence } = contentItem;
  if (!ConceptNameCodeSequence) {
    return {};
  }
  const { CodeValue, CodeMeaning } = ConceptNameCodeSequence ?? {};
  const formattedValue = formatContentItemValue(contentItem) || '[empty]';

  return {
    codeValue: CodeValue,
    codeMeaning: CodeMeaning,
    value: formattedValue,
    nodeIndexesTree: nodeIndexesTree.join('.'),
    isContinuous: continuityOfContent === 'CONTINUOUS',
  };
}
const requiredMeasurementsJSON = {
  'Left Ventrical': [
    {
      'Left Ventricular Peak Early Diastolic Tissue Velocity': "E' lateral", //cm/s
    },
    {
      'Left Ventricular Peak Early Diastolic Tissue Velocity': "E' sept", //cm/s
    },
    {
      'Velocity Time Integral': 'LVOT VTI',
    },
    { 'Interventricular Septum Diastolic Thickness': 'IVSWd' },
    { 'Left Ventricle Internal End Diastolic Dimension': 'LVIDd' },
    { 'Cardiovascular Orifice Diameter': 'LVOT Diam' },
    { 'Peak Velocity': 'LVOT Vmax' },
    { 'Left Ventricle Posterior Wall Diastolic Thickness': 'LVPWD' }, // LVPWD
    { 'Left Ventricular Ejection Fraction': 'EF Biplane' },
    { 'Left Ventricle Internal Systolic Dimension': 'LVIDs' },
    // Raw inputs for derived calculations (prefixed with _ to avoid display)
    { 'Left Ventricular End Diastolic Volume': '_LVEDV_RAW' },
    { 'Left Ventricular End Systolic Volume': '_LVESV_RAW' },
    { 'Left Ventricle Mass': '_LV_MASS_RAW' },
  ],
  //
  'Left Atrium': [
    { 'Left Atrium Systolic Area': 'LA Area' },
    { 'Left Atrium Systolic Volume': '_LA_VOL_RAW' },
  ],
  'Right Atrium': [
    { 'Right Atrium Systolic Area': 'RA Area' },
    {
      'Right Atrium Systolic Pressure': 'RAP',
    },
  ],
  'Aortic Valve': [
    { 'Mean Gradient': 'AV MG' },
    {
      'Velocity Time Integral': 'AV VTI',
    },
    {
      'Peak Velocity': 'AV Vmax',
    },
    {
      'Cardiovascular Orifice Area': 'AVA Vmax',
    },
    {
      'Cardiovascular Orifice Area': 'AVA VTI',
    },
    { 'Peak Gradient': 'AV PG' },
    { 'Pressure Half-Time': 'AR PHT' },
  ],
  'Structure Sinus of Valsalva': [{ Diameter: 'ARD' }],
  Aorta: [
    { 'Ascending Aortic Diameter': 'Asc Aorta' },
    { 'Aortic Arch Diameter': 'Arch' },
    // Philips sends Aortic Root Diameter under Aorta, not Sinus of Valsalva
    { 'Aortic Root Diameter': 'ARD' },
  ],
  'Inferior Vena Cava': [{ 'Inferior Vena Cava Diameter': 'IVC' }],
  'Vena Cava': [{ 'Inferior Vena Cava Diameter': 'IVC' }],
  'Pulmonary Venous Structure': [
    { 'Pulmonary Vein Diastolic Peak Velocity': 'PVein D' },
    { 'Pulmonary Vein Systolic Peak Velocity': 'PVein S' },
  ],

  'Mitral Valve': [
    { 'Deceleration Time': 'Decel time' },
    { 'Mitral Valve A-Wave Peak Velocity': 'A vel' },
    { 'Mitral Valve E-Wave Peak Velocity': 'E vel' },
    { 'Mitral Valve E to A Ratio': 'E/A' },
    { 'Mean Gradient': 'MV MG' },
    { 'Pressure Half-Time': 'PHT' },
    { 'Cardiovascular Orifice Area': 'MVA (PHT)' },
    // GE puts tissue Doppler under Mitral Valve, not Left Ventricle
    { 'Peak Tissue Velocity': "E' lateral" },
    { 'Peak Tissue Velocity': "E' sept" },
    // GE puts Avg E/E' under Mitral Valve as "E Velocity to Average Annulus E Velocity"
    { 'E Velocity to Average Annulus E Velocity': "Avg E/E'" },
  ],
  // Clinic 2 GE machines store TDI under separate annulus finding sites.
  // These run after Mitral Valve and will overwrite with site-specific values if present.
  // If sections absent (Clinic 1 / GE LE10s), no effect — Left Ventrical pass already set them.
  'Lateral Mitral Annulus': [{ 'Peak Tissue Velocity': "E' lateral" }],
  'Medial Mitral Annulus': [{ 'Peak Tissue Velocity': "E' sept" }],

  'Pulmonic Valve': [
    {
      'Peak Velocity': 'PV Vmax',
    },
    {
      'Peak Gradient': 'PV PG',
    },
  ],
  'Aortic Sinotubular Junction': [{ Diameter: 'Aorta STJ' }],
  'Right Ventricle': [
    { 'Right Ventricle Basal Diameter': 'RV Base' },
    { 'Right Ventricle S Velocity': "RVS'" },
    { 'Velocity Time Integral': 'RVOT VTI' },
    { 'Right Ventricular Peak Systolic Pressure': 'RVSP' },
  ],
  'Cardiac Shunt Study': [{ 'Pulmonary-to-Systemic Shunt Flow Ratio': 'Qp:Qs' }],
  'Tricuspid Valve': [
    { 'Tricuspid Annular Plane Systolic Excursion': 'TAPSE' },
    {
      'Peak Velocity': 'TR Vmax',
    },
    {
      'Peak Gradient': 'TR PG',
    },
  ],
  Calculation: [
    { 'LV MI (2D)': 'LVMI' },
    { 'E L AVERAGE': "Avg E/E'" },
    { 'DVI': 'DPI' },
  ],
};

/** Ratios: strip trailing unit "1", use 1 decimal except DPI (2 decimal). */
const RATIO_SHORT_NAMES = ["Avg E/E'", 'DPI', 'E/A'];

/** 2 decimals. MV E/MV A included because E/A divides them — rounding first shifts the ratio. */
const TWO_DECIMAL_FIELDS = new Set([
  "E' lateral",
  "E' sept",
  'DPI',
  "Avg E/E'",
  'E vel',
  'A vel',
]);

/** Whole numbers (0 decimal): indexed/pressure fields */
const WHOLE_NUMBER_FIELDS = new Set(['RVSP', 'LA Vol Ind', 'EF Biplane']);
const METER_PER_SECOND_FIELDS = new Set(['TR Vmax', 'PV Vmax', 'A vel', 'E vel', 'LVOT Vmax']);
const METER_FIELDS = new Set<string>();
/** Times shown in ms. Philips sends Deceleration Time in seconds. */
const MILLISECOND_FIELDS = new Set(['Decel time', 'PHT', 'AR PHT']);

function fractionDigitsForMeasurement(shortName) {
  if (TWO_DECIMAL_FIELDS.has(shortName)) return 2;
  if (WHOLE_NUMBER_FIELDS.has(shortName)) return 0;
  return 1;
}

export function convertUnits(value, shortName) {
  const numPart = parseFloat(value);
  if (isNaN(numPart)) return value; // Return original if not a number

  const numMatch = String(value).match(/^-?\d+(\.\d+)?/);
  let unitPart = numMatch ? String(value).slice(numMatch[0].length).trim() : '';

  // Special handling for ratio fields — strip trailing unit "1"
  if (RATIO_SHORT_NAMES.includes(shortName)) {
    const fd = fractionDigitsForMeasurement(shortName);
    const parts = value.split(' ');
    if (parts.length > 1) {
      return `${parseFloat(parts[0]).toFixed(fd)}`;
    }
    return `${numPart.toFixed(fd)}`;
  }

  const fd = fractionDigitsForMeasurement(shortName);

// Normalize full DICOM unit names to abbreviations before conversion logic.
  // DICOM JSON exports full English unit strings; abbreviations are expected below.
  const UNIT_NORMALIZE: Record<string, string> = {
    millimeter: 'mm',
    'millimeter of mercury column': 'mmHg',
    centimeter: 'cm',
    'square centimeter': 'cm2',
    'square centimeters': 'cm2',
    'square millimeter': 'mm2',
    'square millimeters': 'mm2',
    'mm^2': 'mm2',
    'square meter': 'm2',
    'square meters': 'm2',
    'cubic centimeter': 'cm3',
    'cubic centimeters': 'cm3',
    'cubic millimeter': 'mm3',
    'cubic millimeters': 'mm3',
    'mm^3': 'mm3',
    'cubic meter': 'm3',
    'cubic meters': 'm3',
    gram: 'g',
    percent: '%',
    millisecond: 'ms',
    milliliter: 'mL',
  };
  if (UNIT_NORMALIZE[unitPart]) unitPart = UNIT_NORMALIZE[unitPart];

  if (METER_PER_SECOND_FIELDS.has(shortName)) {
    if (unitPart === 'mm/s') return `${(numPart / 1000).toFixed(fd)} m/s`;
    if (unitPart === 'cm/s') return `${(numPart / 100).toFixed(fd)} m/s`;
    if (unitPart === 'm/s') return `${numPart.toFixed(fd)} m/s`;
  }

  if (MILLISECOND_FIELDS.has(shortName) && ['s', 'sec', 'second', 'seconds'].includes(unitPart)) {
    return `${(numPart * 1000).toFixed(fd)} ms`;
  }

  if (METER_FIELDS.has(shortName)) {
    if (unitPart === 'mm') return `${(numPart / 1000).toFixed(fd)} m`;
    if (unitPart === 'cm') return `${(numPart / 100).toFixed(fd)} m`;
    if (unitPart === 'm') return `${numPart.toFixed(fd)} m`;
  }

// Unit conversions — default behavior for remaining fields
  if (unitPart === 'mm') {
    return `${(numPart / 10).toFixed(fd)} cm`;
  } else if (unitPart === 'mm/s') {
    return `${(numPart / 10).toFixed(fd)} cm/s`;
  } else if (unitPart === 'm') {
    return `${(numPart * 100).toFixed(fd)} cm`;
  } else if (unitPart === 'm/s') {
    return `${(numPart * 100).toFixed(fd)} cm/s`;
  } else if (unitPart.match(/mm\[Hg\]/)) {
    return `${numPart.toFixed(fd)} mmHg`;
  } else if (unitPart.match(/cm\[Hg\]/)) {
    return `${(numPart * 10).toFixed(fd)} mmHg`;
  } else if (unitPart === 'mm2') {
    // Area: square millimetre → square centimetre (some machines, e.g. Philips,
    // report chamber/valve areas in mm². Clinics reporting cm2 are unaffected —
    // they fall through to the default branch unchanged.)
    return `${(numPart / 100).toFixed(fd)} cm²`;
  } else if (unitPart === 'mm3') {
    // Volume: cubic millimetre → millilitre (1 mL = 1000 mm³).
    return `${(numPart / 1000).toFixed(fd)} mL`;
  }
  // All other units — Unicode ²³⁴ via normalizeUnitSuperscriptsDisplay (e.g. ml/m², cm³).
  return `${numPart.toFixed(fd)} ${normalizeUnitSuperscriptsDisplay(unitPart)}`.trim();
}

/**
 * Narrower aliases for a field in one section. The full ARD list includes the
 * generic 'Diameter', which under Aorta would also catch Ascending Aortic Diameter.
 * A field found this way never overwrites one already found in its own section.
 */
const SECTION_ALIAS_OVERRIDES: Record<string, Record<string, string[]>> = {
  Aorta: { ARD: ['Aortic Root', 'Ao Root'] },
};

// Helper function to find matching values within a section
// Uses the alias system to match against all known vendor concept names
function findMatchingValues(sectionData, requiredKey, shortName, aliasOverride?: string[]) {
  const aliases = aliasOverride || measurementAliases[shortName] || [requiredKey];

  if (Array.isArray(sectionData)) {
    const result = [];
    for (let i = 0; i < sectionData.length; i++) {
      const item = sectionData[i];
      if (item.key.includes('Image Mode')) continue;
      if (!aliases.some(alias => item.key.includes(alias))) continue;

      // Measurement Method (G-C036) for this measurement: prefer the value captured
      // directly from the SR's own parent-child nesting (extractMeasurements) — this
      // is the true DICOM relationship and survives any interleaving of unrelated
      // measurements in the flattened list. Only fall back to flat-array proximity
      // scanning (legacy heuristic) when the direct nesting wasn't present/captured.
      // Used by EF Biplane / LVEDV / LVESV to filter by "Method of Disks, Biplane".
      let measurementMethod = item.measurementMethod;
      if (!measurementMethod) {
        const targetMg = item.mgIndex;
        const scanForMethod = (start: number, step: number) => {
          for (let j = start; j >= 0 && j < sectionData.length; j += step) {
            const sj = sectionData[j];
            if (!sj) break;
            if (targetMg !== undefined && sj.mgIndex !== undefined && sj.mgIndex !== targetMg) break;
            if (sj.key.includes('Measurement Method')) return sj.value;
            if (aliases.some(alias => sj.key.includes(alias))) break;
          }
          return null;
        };
        measurementMethod = scanForMethod(i + 1, 1) || scanForMethod(i - 1, -1);
      }

      result.push({
        key: item.key,
        value: convertUnits(item.value, shortName),
        measurementMethod,
        findingSite: item.findingSite,
        derivation: item.derivation,
      });
    }
    return result;
  } else if (sectionData && typeof sectionData === 'object') {
    // Handle Calculation section (object, not array)
    for (const alias of aliases) {
      if (sectionData[alias] !== undefined) {
        return [{ key: alias, value: convertUnits(sectionData[alias], shortName), measurementMethod: null }];
      }
    }
  }
  return [];
}
function averageValues(shortName, values) {
  const nums = values.map(v => parseFloat(v.split(' ')[0]));
  const avg = nums.reduce((sum, num) => sum + num, 0) / nums.length;
  const unit = values[0].split(' ')[1] || '';
  if (shortName === 'LA Vol Ind' || shortName === 'EF Biplane') {
    return `${avg.toFixed(0)} ${unit}`.trim();
  }
  const fd = fractionDigitsForMeasurement(shortName);
  return `${avg.toFixed(fd)} ${unit}`.trim();
}

function cleanKey(key) {
  // Remove special characters and replace spaces with underscores
  return key.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
}
/**
 * Options that change how measurements are resolved for a particular study.
 * `atrialFibrillation` is set from the study's AF mark (sonographer or
 * cardiologist, at any point in the reporting pipeline).
 */
export type MeasurementResolutionOptions = {
  atrialFibrillation?: boolean;
};

// Resolve a single matched set of values to a final value based on shortName rules
/** Exported for the test suite — the per-shortName rules are worth testing directly. */
export function resolveMatchedValue(
  matches,
  shortName,
  options: MeasurementResolutionOptions = {}
) {
  if (matches.length === 0) return null;
  const values = matches.map(m => m.value);

  switch (shortName) {
    case 'ARD':
    case 'LA Area':
    case 'Asc Aorta':
    case 'LVIDs':
    case 'MVA (PHT)':
      return averageValues(shortName, values);

    case 'EF Biplane': {
      // Keep only entries measured with biplane method of disks.
      // If none found (Teichholz-only machines, e.g. GE LE10s), return null → field stays blank.
      const biplaneEF = matches.filter(m => m.measurementMethod === 'Method of Disks, Biplane');
      if (biplaneEF.length === 0) return null;
      // In atrial fibrillation, ejection fraction varies beat to beat with the
      // preceding R-R interval, so a single beat is not representative. Average
      // every biplane EF the sonographer traced instead of taking the last one.
      // Deliberately averages the biplane instances rather than reading the
      // vendor's own mean, which the SR labels single-plane.
      if (options.atrialFibrillation) {
        return averageValues(
          shortName,
          biplaneEF.map(m => m.value)
        );
      }
      return biplaneEF[biplaneEF.length - 1].value;
    }


    case 'AVA VTI':
    case 'AVA Vmax': {
      // When the SR labels the continuity-equation method, take only that method's
      // values (Philips sends only the peak-velocity AVA). Unlabelled: min / max as before.
      const wantVti = shortName === 'AVA VTI';
      const labelled = matches.filter(m => m.measurementMethod);
      let pool = values;
      if (labelled.length > 0) {
        pool = labelled
          .filter(m =>
            wantVti
              ? /velocity time integral|vti/i.test(m.measurementMethod)
              : /peak velocity/i.test(m.measurementMethod)
          )
          .map(m => m.value);
        if (pool.length === 0) return null;
      }
      return wantVti
        ? pool.reduce((min, val) => (parseFloat(val) < parseFloat(min) ? val : min))
        : pool.reduce((max, val) => (parseFloat(val) > parseFloat(max) ? val : max));
    }
    case "E' lateral":
    case "E' sept": {
      // The nested site decides when the SR has one; otherwise max / min below.
      const sited = matches.filter(m => m.findingSite && !m.value.trim().endsWith(' 1'));
      if (sited.length > 0) {
        const want = shortName === "E' lateral" ? /lateral/i : /medial|septal/i;
        return sited.find(m => want.test(m.findingSite))?.value ?? null;
      }
      if (shortName === "E' sept") {
        const septVelocities = values.filter(v => !v.trim().endsWith(' 1'));
        const septCandidates = septVelocities.length > 0 ? septVelocities : values;
        return septCandidates.reduce((min, val) => (parseFloat(val) < parseFloat(min) ? val : min));
      }
      // GE LE10s reports both the E' velocity (m/s) and the E/E' ratio (dimensionless, unit "1")
      // under the same section. Filter out dimensionless ratio values before taking max.
      const latVelocities = values.filter(v => !v.trim().endsWith(' 1'));
      const latCandidates = latVelocities.length > 0 ? latVelocities : values;
      return latCandidates.reduce((max, val) => (parseFloat(val) > parseFloat(max) ? val : max));
    }
    case 'TR Vmax':
    case 'TR PG':
    case 'AV Vmax':
    case 'AV PG':
      return values.reduce((max, val) => (parseFloat(val) > parseFloat(max) ? val : max));

    case "Avg E/E'":
      // GE LE10s provides individual lateral & medial E/E' ratios — average them.
      // For machines that provide a single value, averageValues returns it unchanged.
      return averageValues("Avg E/E'", values);

    case 'LVOT Vmax':
      return (
        matches.find(m => m.key === 'Peak Velocity (11726-7)')?.value ??
        // Philips: 20351-3 with the LVOT as nested site
        matches.find(m => /outflow tract/i.test(m.findingSite || ''))?.value ??
        null
      );

    case "RVS'": {
      // The generic 'Peak Tissue Velocity' alias also matches Philips' E/e' ratio
      // ("Ratio of MV Peak Velocity to LV Peak Tissue Velocity E-Wave", unit "1").
      const velocities = matches.filter(
        m => !/ratio/i.test(m.key) && !m.value.trim().endsWith(' 1')
      );
      return velocities.length > 0 ? velocities[0].value : null;
    }

    /**
     * LV volumes — BIPLANE ONLY, blank otherwise.
     *
     * Dr Shashi, 06 Sep: *"LV volume being calculated by Teicoic method by 2D measurement
     * which is not reliable hence we have to remove that"*, and the consequence he saw:
     * *"Because LV volume coming higher by that method it's automatically saying dilated
     * LV"*. Earlier, 02 Sep: *"It should only pick when we do biplane volume which is more
     * accurate."*
     *
     * This used to PREFER biplane and fall back to whatever else was in the SR, which on a
     * cart that only does M-mode means a Teichholz volume extrapolated from a single
     * parasternal linear dimension. Teichholz assumes a symmetrical ventricle; in a
     * remodelled one it is wrong by tens of millilitres, and ASE has deprecated it for
     * volumes. That wrong volume then drove LVEDVI, which drove the wording engine into
     * writing "Moderately dilated" on a normal ventricle — the exact chain behind the
     * notice in his screenshot (LV EDV index 77 ml/m²).
     *
     * So: no fallback. Same rule `EF Biplane` above has always followed — a value we
     * cannot compute honestly is left blank, because a blank prompts a sonographer to
     * trace the volume while a plausible wrong number does not.
     *
     * ⚠️ On a Teichholz-only machine (GE LE10s) LVEDVI and LVESVI now read BLANK where a
     * figure used to appear. Intended, and worth telling clinics before they see it.
     */
    case '_LVEDV_RAW':
    case '_LVESV_RAW': {
      // Exclude per-BSA indexed values (unit contains '/m', e.g. 'ml/m²') — the derived
      // index calc needs the ABSOLUTE volume, else it double-indexes.
      //
      // No fallback to the indexed entries when there is no absolute one, which is a
      // change: this line used to read `nonIndexed.length > 0 ? nonIndexed : matches`, so
      // a cart publishing ONLY the index handed an already-indexed figure back as if it
      // were a raw volume, and LVEDVI then divided it by BSA a second time. A silently
      // double-indexed volume is the same class of defect as the Teichholz fallback above
      // and gets the same answer: leave it blank.
      const usable = matches.filter(m => !m.value.includes('/m'));
      const biplaneVol = usable.filter(m => m.measurementMethod === 'Method of Disks, Biplane');
      if (biplaneVol.length === 0) {
        return null;
      }
      return biplaneVol[biplaneVol.length - 1].value;
    }

    case '_LA_VOL_RAW': {
      // Exclude per-BSA indexed values (unit contains '/m' e.g. 'mL/m2', 'milliliter/meter2').
      // Some machines include LA Volume Index alongside raw volumes under the same concept name;
      // the index is last and would be picked by the default last-value rule.
      const rawOnly = values.filter(v => !v.includes('/m'));
      return rawOnly.length > 0 ? rawOnly[rawOnly.length - 1] : values[values.length - 1];
    }

    case 'E vel':
    case 'A vel':
    case 'Decel time':
    case 'E/A':
      // Mitral inflow: the machine's average when the SR marks one (Derivation = Mean),
      // else the first beat.
      return matches.find(m => /mean/i.test(m.derivation || ''))?.value ?? values[0];

    default:
      return values[0];
  }
}

// Find the section data for a given section name using alias resolution
function findSectionData(sectionName, data) {
  const canonicalSite = resolveFindingSite(sectionName);
  let sectionData = data[canonicalSite];
  if (!sectionData) {
    for (const dataKey of Object.keys(data)) {
      if (resolveFindingSite(dataKey) === canonicalSite) {
        sectionData = data[dataKey];
        break;
      }
    }
  }
  return sectionData;
}

function filterMeasurements(requiredMeasurements, data, options: MeasurementResolutionOptions = {}) {
  const result = {};
  // Collect unfound measurements for cross-section fallback
  const unfound = [];

  // Pass 1: Primary section-based matching
  for (const [sectionName, measurements] of Object.entries(requiredMeasurements)) {
    const sectionData = findSectionData(sectionName, data);
    if (!sectionData) {
      // Queue all measurements from missing sections for fallback
      measurements.forEach(item => {
        const fullName = Object.keys(item)[0];
        const shortName = item[fullName];
        unfound.push({ fullName, shortName });
      });
      continue;
    }

    measurements.forEach(item => {
      const fullName = Object.keys(item)[0];
      const shortName = item[fullName];

      const aliasOverride = SECTION_ALIAS_OVERRIDES[sectionName]?.[shortName];
      if (aliasOverride && result[cleanKey(shortName)]) return;

      const matches = findMatchingValues(sectionData, fullName, shortName, aliasOverride);
      const finalValue = resolveMatchedValue(matches, shortName, options);

      if (finalValue) {
        result[cleanKey(shortName)] = finalValue.replace(/\^/g, '');
      } else {
        unfound.push({ fullName, shortName });
      }
    });
  }

  // Pass 2: Cross-section fallback — search ALL sections for unfound measurements.
  // Fields that must NOT fall through here:
  // Either because their concept names are shared across valve sections (contamination risk)
  // or because the field should stay blank when the section is absent (e.g. STJ, ARD on LE10s).
  const SECTION_SPECIFIC_FIELDS = new Set([
    'PHT', 'AR PHT', 'MV MG', 'AV MG', 'MVA (PHT)', "Avg E/E'", 'RVOT VTI', 'LVOT VTI', 'AV VTI',
    'ARD',        // absent on GE LE10s — Pass 2 picks LVOT Diam from LV section
    'Aorta STJ',  // absent on all current machines — Pass 2 picks ARD/Asc Aorta
    'TR Vmax',    // absent when no TR jet measured — Pass 2 picks AoV/PV peak velocity
    'TR PG',      // same as TR Vmax
    'RA Area',    // alias includes generic 'Area' which contaminates Pass 2
    "E' lateral", // 'Peak Tissue Velocity' also matches RVS' in Right Ventricle section
    "E' sept",    // same — prevent Pass 2 from picking RVS'
  ]);

  if (unfound.length > 0) {
    const allSectionArrays = Object.values(data).filter(Array.isArray);

    unfound.forEach(({ fullName, shortName }) => {
      if (result[cleanKey(shortName)]) return; // Already found in pass 1
      if (SECTION_SPECIFIC_FIELDS.has(shortName)) return; // Don't cross-contaminate

      for (const sectionData of allSectionArrays) {
        const matches = findMatchingValues(sectionData, fullName, shortName);
        const finalValue = resolveMatchedValue(matches, shortName, options);

        if (finalValue) {
          result[cleanKey(shortName)] = finalValue.replace(/\^/g, '');
          break; // Found it, stop searching other sections
        }
      }
    });
  }

  return result;
}

function transformCardiacData(data) {
  const output = {};

  for (const section in data) {
    // Get section abbreviation (first letters of each word)
    const prefix = section
      .split(/\s+/)
      .map(word => word?.[0]?.toUpperCase() || '')
      .join('');

    const items = data[section];
    // const seenKeys = new Set(); // For skipping duplicate "Image Mode" entries etc.

    if (!items || !Array.isArray(items) || items.length === 0) continue;

    let imageModeValue = '';
    for (const item of items) {
      const rawKey = item?.key || '';
      const match = rawKey.match(/^(.+?)\s*\(/);
      const label = match ? match[1].trim() : rawKey.trim();
      const keyName = label
        .replace(/[^a-zA-Z0-9]/g, ' ')
        .trim()
        .split(/\s+/)
        .map(word => word?.trim()?.charAt(0).toUpperCase() + word?.slice(1) || '')
        .join('');
      let value = item?.value || '';

      if (rawKey.includes('Image Mode')) {
        imageModeValue = value
          .replace(/[^a-zA-Z0-9]/g, ' ')
          .trim()
          .split(/\s+/)
          .map(word => word?.[0]?.toUpperCase() || '')
          .join('');
        continue; // skip storing imageMode directly
      }

      // Skip duplicate keys
      // if (seenKeys.has(keyName)) continue;
      // seenKeys.add(keyName);
      const finalKey = prefix + imageModeValue + keyName;

      const twoDecimalKeys = [];
      const wholeNumKeys = [
        'LA2MLeftAtrialEndSystolicVolumeIndex',
      ];

      // Determine number of decimals
      let decimalPlaces = 1;
      if (twoDecimalKeys.includes(finalKey)) decimalPlaces = 2;
      if (wholeNumKeys.includes(finalKey)) decimalPlaces = 0;

      // Normalize numeric format
      const cleanNumber = num => (!isNaN(num) ? Number(num).toFixed(decimalPlaces) : num);

      // Handle velocity conversion
      if (value?.includes('mm/s') || value?.includes('cm/s') || value?.includes('m/s')) {
        const [numStr, unit] = value.split(' ');
        const num = parseFloat(numStr);

        if (unit === 'mm/s') {
          value = (num / 10).toFixed(decimalPlaces) + ' cm/s';
        } else if (unit === 'm/s') {
          value = (num * 100).toFixed(decimalPlaces) + ' cm/s';
        } else if (unit === 'cm/s') {
          value = (num).toFixed(decimalPlaces) + ' cm/s';
        }

        value = value.replace(/\^/g, '');
      } else {
        // Round only the leading numeric portion — never touch unit characters (e.g. "2" in "cm2")
        const [numStr, ...unitParts] = (value || '').split(' ');
        const num = parseFloat(numStr);
        const unit = unitParts.join(' ');
        if (!isNaN(num)) {
          value = cleanNumber(num) + (unit ? ' ' + normalizeUnitSuperscriptsDisplay(unit.replace(/\^/g, '')) : '');
        } else {
          value = value?.replace(/\^/g, '');
        }
      }

      output[finalKey] = value;
    }
  }

  return output;
}

// function transformCardiacData(data) {
//   const output = {};
//   const valueMap = {}; // Temporary storage for summing duplicates
//   const countMap = {}; // Count of values for averaging

//   for (const section in data) {
//     // Get section abbreviation (first letters of each word)
//     const prefix = section
//       .split(/\s+/)
//       .map(word => word[0].toUpperCase())
//       .join('');

//     const items = data[section];
//     let imageModeValue = '';

//     for (const item of items) {
//       const rawKey = item.key;
//       const match = rawKey.match(/^(.+?)\s*\(/);
//       const label = match ? match[1].trim() : rawKey.trim();
//       const keyName = label
//         .replace(/[^a-zA-Z0-9]/g, ' ')
//         .trim()
//         .split(/\s+/)
//         .join('');

//       const valueStr = item.value;
//       const valueNum = parseFloat(valueStr);

//       // Build ImageMode prefix only once per loop
//       if (rawKey.includes('Image Mode')) {
//         imageModeValue = valueStr
//           .replace(/[^a-zA-Z0-9]/g, ' ')
//           .trim()
//           .split(/\s+/)
//           .map(word => word[0].toUpperCase())
//           .join('');
//         continue; // skip storing imageMode directly
//       }

//       const finalKey = prefix + imageModeValue + keyName;

//       // Only store numeric values for averaging
//       if (!isNaN(valueNum)) {
//         valueMap[finalKey] = (valueMap[finalKey] || 0) + valueNum;
//         countMap[finalKey] = (countMap[finalKey] || 0) + 1;
//       } else {
//         // For non-numeric values, just store the first one
//         if (!(finalKey in output)) {
//           output[finalKey] = valueStr;
//         }
//       }
//     }
//   }

//   // Final pass to calculate averages
//   for (const key in valueMap) {
//     const avg = valueMap[key] / countMap[key];
//     output[key] = avg.toFixed(1); // Round to 1 decimal place
//   }

//   return output;
// }

/**
 * Find an SR content item's value by its exact `codeMeaning` text — the raw vendor
 * concept name (e.g. "XAEA12"), no `measurementAliases.ts` matching involved.
 *
 * A dynamic field (18 Sep, "catch this exact SR measurement" ask) binds itself to
 * one of these raw names precisely because it ISN'T in the alias dictionary — it
 * has no canonical short name, no CHAIN_FIELDS entry, nothing to fuzzy-match
 * against. Exact string equality only, by design: the clinic typed this name
 * themselves from their own machine's output, so no vendor-wording tolerance is
 * being solved here, unlike `measurementAliases.ts`'s substring matching.
 */
export function findSRValueByExactConceptName(contentItems, conceptName: string): string | null {
  if (!Array.isArray(contentItems) || !conceptName) {
    return null;
  }
  for (const item of contentItems) {
    if (item?.codeMeaning === conceptName && item?.value != null && String(item.value) !== '') {
      return String(item.value);
    }
    if (Array.isArray(item?.contentItems)) {
      const found = findSRValueByExactConceptName(item.contentItems, conceptName);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

// Extract BSA numeric value from the SR container data
function extractBSAFromContainer(contentItems) {
  if (!Array.isArray(contentItems)) return null;
  for (const item of contentItems) {
    if (item.codeMeaning === 'Body Surface Area' && item.value) {
      const num = parseFloat(item.value);
      return isNaN(num) ? null : num;
    }
    // Recurse into nested containers
    if (Array.isArray(item.contentItems)) {
      const found = extractBSAFromContainer(item.contentItems);
      if (found) return found;
    }
  }
  return null;
}

// A heart rate is only believable inside these bounds. Anything outside is a
// misparse (an R-R interval read as bpm, a percentage, a stray measurement)
// rather than a rate, and printing it on a report is worse than printing nothing.
const HR_MIN_BPM = 20;
const HR_MAX_BPM = 250;

const plausibleHeartRate = (bpm: number | null): number | null =>
  bpm != null && isFinite(bpm) && bpm >= HR_MIN_BPM && bpm <= HR_MAX_BPM ? bpm : null;

/**
 * Does this SR concept name denote a heart rate?
 *
 * Deliberately wider than an equality test. The original exact match on
 * "heart rate" / "heartrate" / "hr" is why heart rate was not appearing on our
 * reports while Piccolo showed it (Dr Shashi, 19 Aug): carts write the concept
 * under several names, and one spelling we did not list means an empty row.
 *
 * "HR" is matched only as a whole word — as a substring it collides with
 * unrelated concepts, and a wrong number is worse than a blank one.
 */
function isHeartRateConcept(codeMeaning: string): boolean {
  const cm = codeMeaning.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!cm) {
    return false;
  }

  // Per-trace Doppler rates are excluded. A cart records a heart rate alongside
  // each Doppler sweep, computed from that sweep alone; on the 19 Aug capture one
  // of these read 237 while the acquisition tag and the image overlay both read
  // 84. Those are measurements OF a trace, not the rate for the study, and a
  // mis-traced sweep yields a number that is wrong but perfectly plausible.
  if (/\b(lvdp|rvdp|mvdp|avdp|avdcw|pvdcw|tvdcw|tvmm|doppler|trace|sweep)\b/.test(cm)) {
    return false;
  }

  // Anchored, not a substring search. The previous version accepted any concept
  // containing "hr" as a word and anything merely mentioning a rate, which is how
  // a per-trace value got in.
  return /^(mean |average |avg )?(heart rate|heartrate|pulse rate|cardiac frequency|hr)$/.test(cm);
}

/** R-R interval in ms → bpm. An R-R interval is the reciprocal of the rate. */
function heartRateFromRR(value: string): number | null {
  const ms = parseFloat(value);
  if (isNaN(ms) || ms <= 0) {
    return null;
  }
  // Some carts write R-R in seconds. Below 10 cannot be milliseconds (that would
  // be a 6000 bpm rate), so treat it as seconds.
  const intervalMs = ms < 10 ? ms * 1000 : ms;
  return plausibleHeartRate(60000 / intervalMs);
}

const isRRConcept = (codeMeaning: string): boolean =>
  /\br\s*-?\s*r\b.*(interval|duration)|rr\s*interval/i.test(codeMeaning.trim());

// Extract Heart Rate (bpm) from the SR container. Heart rate is not a
// demographic DICOM tag — it lives in the SR measurement content (typically
// under "Patient Characteristics", codeMeaning "Heart Rate"). Returns a display
// string like "72 bpm", or null when the SR carries no heart rate.
//
// A direct heart-rate concept always wins over an R-R interval: the R-R route is
// arithmetic on one beat, so it disagrees with the cart's own averaged figure.
// The whole container is therefore scanned for a direct rate before any R-R
// value is considered.
export function extractHeartRateFromContainer(contentItems): string | null {
  const direct = findHeartRateItem(contentItems, false);
  if (direct) {
    return direct;
  }
  return findHeartRateItem(contentItems, true);
}

function findHeartRateItem(contentItems, allowRR: boolean): string | null {
  const direct = findDirectHeartRateItem(contentItems);
  if (direct != null) {
    return direct;
  }
  if (!allowRR) {
    return null;
  }
  // Multiple R-R intervals can appear across a container (one per cycle/beat).
  // A single interval is one heartbeat's worth of arithmetic, not the study's
  // rate — collect every R-R-derived bpm found and average them.
  const rrRates = collectRRHeartRates(contentItems);
  if (!rrRates.length) {
    return null;
  }
  const avg = rrRates.reduce((sum, v) => sum + v, 0) / rrRates.length;
  return `${Math.round(avg)} bpm`;
}

function findDirectHeartRateItem(contentItems): string | null {
  if (!Array.isArray(contentItems)) {
    return null;
  }
  for (const item of contentItems) {
    const cm = String(item?.codeMeaning || '');
    if (item?.value != null && String(item.value) !== '' && isHeartRateConcept(cm)) {
      const bpm = plausibleHeartRate(parseFloat(item.value));
      if (bpm != null) {
        // Preserve an explicit unit if the SR carried one, else default to bpm.
        const unit = String(item.value)
          .replace(/^-?[\d.]+\s*/, '')
          .trim();
        return `${Math.round(bpm)} ${unit || 'bpm'}`;
      }
    }
    if (Array.isArray(item?.contentItems)) {
      const found = findDirectHeartRateItem(item.contentItems);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

function collectRRHeartRates(contentItems): number[] {
  if (!Array.isArray(contentItems)) {
    return [];
  }
  const rates: number[] = [];
  for (const item of contentItems) {
    const cm = String(item?.codeMeaning || '');
    if (item?.value != null && String(item.value) !== '' && isRRConcept(cm)) {
      const bpm = heartRateFromRR(String(item.value));
      if (bpm != null) {
        rates.push(bpm);
      }
    }
    if (Array.isArray(item?.contentItems)) {
      rates.push(...collectRRHeartRates(item.contentItems));
    }
  }
  return rates;
}

/**
 * The slice of DicomMetadataStore the heart-rate readers need. Declared
 * structurally so the tests can pass a two-line stub instead of a real store.
 */
type StudyInstance = {
  Modality?: string;
  HeartRate?: unknown;
  ['00181088']?: { Value?: unknown[] };
  Manufacturer?: unknown;
  ManufacturerModelName?: unknown;
  ['00080070']?: { Value?: unknown[] };
  ['00081090']?: { Value?: unknown[] };
};
type StudySeries = {
  SeriesInstanceUID?: string;
  Modality?: string;
  instances?: StudyInstance[];
};
type StudyLookup = { getStudy?: (studyUID: string) => { series?: StudySeries[] } | undefined };

/** A DICOM value as either a naturalized scalar or a raw `{ Value: [...] }` element. */
const tagValue = (raw: unknown): string => {
  const unwrapped = (raw as { Value?: unknown[] })?.Value?.[0] ?? raw;
  return unwrapped == null ? '' : String(unwrapped);
};

/**
 * Heart rate carried on the image instances themselves, DICOM tag
 * HeartRate (0018,1088).
 *
 * It sits on the acquisition rather than in the SR, so a study whose SR omits the
 * concept can still state its rate — the common case on the carts in use.
 *
 * THE LOWEST RATE, NOT THE AVERAGE
 * --------------------------------
 * This originally averaged every instance in the study. Defensible for a resting
 * echo, where the whole study is one state and averaging smooths beat-to-beat
 * variation. Wrong for a stress echo, which is several scans of the same patient in
 * deliberately different states — rest, each exercise stage, peak, recovery. Cor
 * Cardiology (14 Sep) reported a rate of 91 where the cart said 70: that 91 was the
 * mean of a rest scan near 78 and a peak near 130, a figure describing no moment of
 * the study.
 *
 * The row is labelled "Heart rate" and the stress template's prose reads "Resting
 * heart rate was …", so the value wanted is the RESTING one. Their cardiologist's
 * rule (14 Sep, via Nikhil) is the **lowest** rate in the study: the patient is
 * slowest at rest, so the minimum is the resting rate. That is taken literally here
 * rather than reasoned about — no assumption that the rest scan comes first, which
 * recovery can undercut anyway.
 *
 * ⚠️ A minimum is decided by ONE clip, where an average absorbed a bad reading. The
 * plausibility floor rejects nonsense, but a mis-tagged yet believable low value
 * becomes the reported resting rate. That trade was the clinician's call.
 *
 * A resting echo is barely affected: one state, so the spread is a few beats.
 *
 * Used only as a fallback behind the SR, never in preference to it.
 */
export function getHeartRateFromStudyTags(
  StudyInstanceUID: string | null | undefined,
  metadataStore: StudyLookup = DicomMetadataStore
): number | null {
  if (!StudyInstanceUID) {
    return null;
  }
  try {
    const study = metadataStore?.getStudy?.(StudyInstanceUID);
    let lowest: number | null = null;

    for (const series of study?.series || []) {
      // An SR is measurement content, not an acquisition. Its rates are the cart's
      // own derived figures, including per-trace ones that the SR reader rejects by
      // concept name — none of which can be identified from a bare tag here.
      if (String(series?.Modality || '') === 'SR') {
        continue;
      }
      for (const instance of series?.instances || []) {
        const bpm = plausibleHeartRate(
          parseFloat(tagValue(instance?.HeartRate ?? instance?.['00181088']))
        );
        if (bpm != null && (lowest == null || bpm < lowest)) {
          lowest = bpm;
        }
      }
    }

    return lowest;
  } catch {
    /* metadata not loaded — the row simply stays empty */
  }
  return null;
}

/**
 * Single source of truth for the {{heart_rate}} row. Chain, in priority order:
 *   1. a heart rate stated in the report's SR (the cart's own averaged figure)
 *   2. an R-R interval in that SR, converted
 *   3. the LOWEST DICOM HeartRate (0018,1088) across the study's images
 *
 * Returns a display string like "72 bpm", or null when no source has one. A rate
 * is never invented: every step reads a value some device recorded.
 */
export function resolveReportHeartRate(
  contentItems,
  StudyInstanceUID: string | null | undefined,
  metadataStore: StudyLookup = DicomMetadataStore
): string | null {
  // The acquisition tag comes FIRST. This was originally the other way round, on
  // the reasoning that the SR holds the cart's own averaged figure — the first live
  // capture (19 Aug) disproved it: the SR offered 237 where the tag and the image
  // overlay both read 84. DICOM HeartRate (0018,1088) is also the source Piccolo
  // reads, which is what the original request was about.
  const fromTags = getHeartRateFromStudyTags(StudyInstanceUID, metadataStore);
  if (fromTags != null) {
    return `${Math.round(fromTags)} bpm`;
  }

  return extractHeartRateFromContainer(contentItems);
}

// Blood pressure codes in the echo SR, TID 5201 Patient Characteristics (PS3.16).
// Current SNOMED codes plus the older SRT codes some carts (GE v206) still write.
const SYSTOLIC_BP_CODES = new Set(['271649006', 'F-008EC']);
const DIASTOLIC_BP_CODES = new Set(['271650006', 'F-008ED']);

/** "120 mm[Hg]" / "120 mmHg" / "120" → 120. Any other unit (e.g. kPa) → null. */
function bpMmHg(value: unknown): number | null {
  const text = String(value ?? '').trim();
  const match = /^(\d+(?:\.\d+)?)\s*(.*)$/.exec(text);
  if (!match) {
    return null;
  }
  const unit = match[2].replace(/[\s[\]]/g, '').toLowerCase();
  if (unit && unit !== 'mmhg') {
    return null;
  }
  const n = parseFloat(match[1]);
  return n > 0 ? n : null;
}

/**
 * Blood pressure from the report's SR as "120/80", or null.
 *
 * Matched by code value first, then by concept name. Both halves are required:
 * a lone systolic is half a measurement, so the slot stays blank for the
 * clinician to fill instead.
 */
export function extractBloodPressureFromContainer(contentItems): string | null {
  let systolic: number | null = null;
  let diastolic: number | null = null;
  type SrItem = {
    codeValue?: unknown;
    codeMeaning?: unknown;
    value?: unknown;
    contentItems?: SrItem[];
  };
  const walk = (items: SrItem[] | null | undefined) => {
    if (!Array.isArray(items)) {
      return;
    }
    for (const item of items) {
      const code = String(item?.codeValue || '');
      const cm = String(item?.codeMeaning || '')
        .trim()
        .toLowerCase();
      if (systolic == null && (SYSTOLIC_BP_CODES.has(code) || cm === 'systolic blood pressure')) {
        systolic = bpMmHg(item?.value);
      } else if (
        diastolic == null &&
        (DIASTOLIC_BP_CODES.has(code) || cm === 'diastolic blood pressure')
      ) {
        diastolic = bpMmHg(item?.value);
      }
      if (Array.isArray(item?.contentItems)) {
        walk(item.contentItems);
      }
    }
  };
  walk(contentItems);
  if (systolic == null || diastolic == null || systolic <= diastolic) {
    return null;
  }
  return `${Math.round(systolic)}/${Math.round(diastolic)}`;
}

/**
 * The scanner that acquired the study, "Manufacturer Model", from the General
 * Equipment tags (0008,0070) + (0008,1090) on the image instances. Image series
 * are read before the SR; the most common pair wins if a study mixes carts.
 */
export function getMachineFromStudyTags(
  StudyInstanceUID: string | null | undefined,
  metadataStore: StudyLookup = DicomMetadataStore
): string | null {
  if (!StudyInstanceUID) {
    return null;
  }
  try {
    const study = metadataStore?.getStudy?.(StudyInstanceUID);
    const imageCounts = new Map<string, number>();
    const srCounts = new Map<string, number>();
    for (const s of study?.series || []) {
      const counts = String(s?.Modality) === 'SR' ? srCounts : imageCounts;
      for (const instance of s?.instances || []) {
        const make = tagValue(instance?.Manufacturer ?? instance?.['00080070']).trim();
        const model = tagValue(instance?.ManufacturerModelName ?? instance?.['00081090']).trim();
        // Some carts repeat the maker inside the model name; print it once.
        const name =
          make && model.toLowerCase().startsWith(make.toLowerCase())
            ? model
            : [make, model].filter(Boolean).join(' ');
        if (name) {
          counts.set(name, (counts.get(name) || 0) + 1);
        }
      }
    }
    const counts = imageCounts.size ? imageCounts : srCounts;
    let best: string | null = null;
    let bestCount = 0;
    counts.forEach((count, name) => {
      if (count > bestCount) {
        best = name;
        bestCount = count;
      }
    });
    return best;
  } catch {
    /* metadata not loaded — the row simply stays empty */
  }
  return null;
}

// Fallback: extract Patient Height (meters) + Patient Weight (kg) from SR
// so we can compute BSA via DuBois when SR doesn't include "Body Surface Area".
// GE LE10s and similar machines omit BSA but include height/weight.
export function extractHeightWeightFromContainer(contentItems) {
  let heightM: number | null = null;
  let weightKg: number | null = null;
  const walk = (items: any[]) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      const cm = item?.codeMeaning;
      if (cm === 'Patient Height' || cm === 'Body Height' || cm === 'Height') {
        const num = parseFloat(item?.value);
        if (!isNaN(num) && heightM == null) heightM = num;
      }
      if (cm === 'Patient Weight' || cm === 'Body Weight' || cm === 'Weight') {
        const num = parseFloat(item?.value);
        if (!isNaN(num) && weightKg == null) weightKg = num;
      }
      if (Array.isArray(item?.contentItems)) walk(item.contentItems);
    }
  };
  walk(contentItems);
  return { heightM, weightKg };
}

// Normalise a height value to metres. DICOM Patient Size (0010,1020) is metres
// (e.g. 1.75), but height embedded in an SR is sometimes recorded in cm. Anything
// greater than 3 cannot be a human height in metres, so treat it as cm → m.
function normalizeHeightToMeters(height: number | null): number | null {
  if (height == null || isNaN(height) || height <= 0) return null;
  return height > 3 ? height / 100 : height;
}

// DuBois BSA, the formula in the GE Vivid/EchoPAC reference manual (our main machine source).
function calcBsa(heightM: number | null, weightKg: number | null) {
  const h = normalizeHeightToMeters(heightM);
  if (!h || !weightKg) return null;
  const bsa = echoBsa(h * 100, weightKg);
  return bsa == null || isNaN(bsa) ? null : parseFloat(bsa.toFixed(2));
}

/**
 * Single source of truth for a report's BSA (m²). Chain, in priority order:
 *   1. BSA recorded in the SR ("Body Surface Area"), wherever it is nested.
 *   2. Calculated (DuBois) from height + weight found inside the SR.
 *   3. Calculated (DuBois) from the study-level demographic height + weight.
 * Returns a number rounded to 2dp, or null when nothing is available.
 * Used for BOTH the derived indexed measurements and the demographics header so
 * BSA appears on every report — from the SR when present, calculated otherwise.
 */
export function resolveReportBSA(
  contentItems,
  fallbackHeightM: number | null = null,
  fallbackWeightKg: number | null = null
): number | null {
  const srBsa = extractBSAFromContainer(contentItems);
  if (srBsa) {
    return parseFloat(Number(srBsa).toFixed(2));
  }
  const { heightM, weightKg } = extractHeightWeightFromContainer(contentItems);
  return calcBsa(heightM, weightKg) ?? calcBsa(fallbackHeightM, fallbackWeightKg);
}

// Parse a numeric value from a measurement string like "70.3 ml" → 70.3
function parseNumericValue(str) {
  if (!str) return null;
  const num = parseFloat(str);
  return isNaN(num) ? null : num;
}

// "0.87 m/s" / "10.70 cm/s" / "107 mm/s" → cm/s, or null for anything else.
function velocityCmPerSec(str) {
  const num = parseNumericValue(str);
  if (!num) return null;
  const unit = String(str).trim().split(/\s+/)[1];
  if (unit === 'm/s') return num * 100;
  if (unit === 'cm/s') return num;
  if (unit === 'mm/s') return num / 10;
  return null;
}

// Look up a measurement directly from `findings` (pre-convertUnits raw values)
// and return its numeric value normalised to centimetres.
// Used by derived calculations that need precise inputs (e.g. DPI = LVOT VTI / AV VTI),
// where the display string would be lossy after METER_FIELDS rounding.
function findRawCmFromFindings(findings, shortName, sectionName) {
  if (!findings) return null;
  const aliases = measurementAliases[shortName] || [];
  if (aliases.length === 0) return null;

  const canonicalSite = resolveFindingSite(sectionName);
  let sectionData = findings[canonicalSite];
  if (!sectionData) {
    for (const dataKey of Object.keys(findings)) {
      if (resolveFindingSite(dataKey) === canonicalSite) {
        sectionData = findings[dataKey];
        break;
      }
    }
  }
  if (!Array.isArray(sectionData)) return null;

  for (const item of sectionData) {
    if (!item || typeof item.key !== 'string') continue;
    if (item.key.includes('Image Mode')) continue;
    if (!aliases.some(alias => item.key.includes(alias))) continue;

    const raw = item.value;
    if (typeof raw !== 'string') continue;
    const num = parseFloat(raw);
    if (isNaN(num)) continue;
    const unit = raw.replace(/^-?[\d.]+\s*/, '').trim().toLowerCase();
    if (unit === 'mm' || unit === 'millimeter') return num / 10;
    if (unit === 'm' || unit === 'meter') return num * 100;
    return num; // cm / centimeter / unitless treated as cm
  }
  return null;
}

// Compute derived fields from already-extracted measurements
// These fields are NOT directly present in most SR files and must be calculated
function computeDerivedFields(measurements, bsa, findings = null) {
  const derived = {};

  // ── LV Mass (cube / Devereux / ASE formula) ───────────────────────────
  // LV Mass (g) = 0.8 × [1.04 × ((IVSd + LVIDd + LVPWd)^3 − (LVIDd)^3)] + 0.6
  // Inputs in cm (already normalised to cm by convertUnits).
  // Only computed when SR did not provide LV Mass directly.
  let lvMass = parseNumericValue(measurements['LVMASSRAW']);
  if (!lvMass) {
    const ivsd = parseNumericValue(measurements['IVSWd']);
    const lvidd = parseNumericValue(measurements['LVIDd']);
    const lvpwd = parseNumericValue(measurements['LVPWD']);
    if (ivsd && lvidd && lvpwd) {
      const sum = ivsd + lvidd + lvpwd;
      lvMass = 0.8 * (1.04 * (Math.pow(sum, 3) - Math.pow(lvidd, 3))) + 0.6;
      derived['LV_Mass'] = `${lvMass.toFixed(0)} g`;
    }
  }

  // ── LVMI (Left Ventricular Mass Index) ────────────────────────────────
  // Formula: LVMI = LV Mass / BSA
  // Units: g/m²
  if (lvMass && bsa) {
    derived['LVMI'] = `${(lvMass / bsa).toFixed(0)} ${normalizeUnitSuperscriptsDisplay('g/m2')}`;
  }

  // ── LVEDVI (Left Ventricular End-Diastolic Volume Index) ──────────────
  // Formula: LVEDVI = LVEDV / BSA
  // Units: ml/m²
  const lvedv = parseNumericValue(measurements['LVEDVRAW']);
  if (lvedv && bsa) {
    derived['LVEDVI'] = `${(lvedv / bsa).toFixed(0)} ${normalizeUnitSuperscriptsDisplay('ml/m2')}`;
  }

  // ── LVESVI (Left Ventricular End-Systolic Volume Index) ───────────────
  // Formula: LVESVI = LVESV / BSA
  // Units: ml/m²
  const lvesv = parseNumericValue(measurements['LVESVRAW']);
  if (lvesv && bsa) {
    derived['LVESVI'] = `${(lvesv / bsa).toFixed(0)} ${normalizeUnitSuperscriptsDisplay('ml/m2')}`;
  }

  // ── LA Vol Ind (Left Atrial Volume Index) ───────────────────────────────
  // Formula: LA Vol Ind = LAESV (biplane MOD BP, last value) / BSA
  // Units: ml/m²
  const laVol = parseNumericValue(measurements['LAVOLRAW']);
  if (laVol && bsa) {
    derived['LA_Vol_Ind'] = `${(laVol / bsa).toFixed(0)} ${normalizeUnitSuperscriptsDisplay('ml/m2')}`;
  }

  // ── Avg E/E' when the SR has no average (e.g. Philips sends only per-site ratios) ──
  // ASE 2025: E divided by the mean of septal and lateral e'. Uses the printed values,
  // same as measurementChain Avg_EE. Only when both sites were measured separately;
  // equal values mean one e' filled both fields.
  if (!measurements['Avg_EE']) {
    const e = velocityCmPerSec(measurements['E_vel']);
    const lat = velocityCmPerSec(measurements['E_lateral']);
    const sep = velocityCmPerSec(measurements['E_sept']);
    if (e && lat && sep && lat !== sep) {
      derived['Avg_EE'] = (e / ((lat + sep) / 2)).toFixed(fractionDigitsForMeasurement("Avg E/E'"));
    }
  }

  // ── ARD Index / Asc Aorta Index (aortic dimensions indexed to BSA) ─────
  // Formula: index = diameter / BSA
  // Units: cm/m² — ARD and Asc Aorta are normalised to cm by convertUnits, and
  // aortic dimensions are conventionally indexed in cm/m² (ASE/ESC).
  // Indexed off the SAME display value that prints on the report, so the index
  // always agrees with the diameter beside it rather than with an unrounded
  // intermediate the clinician cannot see.
  const ard = parseNumericValue(measurements['ARD']);
  if (ard && bsa) {
    derived['ARD_Index'] = `${(ard / bsa).toFixed(1)} ${normalizeUnitSuperscriptsDisplay('cm/m2')}`;
  }

  const ascAorta = parseNumericValue(measurements['Asc_Aorta']);
  if (ascAorta && bsa) {
    derived['Asc_Aorta_Index'] = `${(ascAorta / bsa).toFixed(1)} ${normalizeUnitSuperscriptsDisplay('cm/m2')}`;
  }

  // ── DPI (Dimensionless Performance Index) ──────────────────────────────
  // Primary source: SR Calculation section ("DVI" key)
  // Fallback: derive from LVOT VTI / AV VTI using raw findings (cm), bypassing
  // the LVOT VTI display value which is rounded to 1 decimal in metres
  // (METER_FIELDS) and loses precision (e.g. 19.21 cm → "0.2 m" → 20 cm).
  if (!measurements['DPI']) {
    const lvotCm = findRawCmFromFindings(findings, 'LVOT VTI', 'Left Ventrical');
    const avCm = findRawCmFromFindings(findings, 'AV VTI', 'Aortic Valve');
    if (lvotCm && avCm && avCm !== 0) {
      derived['DPI'] = `${(lvotCm / avCm).toFixed(2)}`;
    }
  }

  // ── RVSP (Right Ventricular Systolic Pressure) ────────────────────────
  // Formula: RVSP = 4 × (TR Vmax)² + RAP
  // Units: mmHg
  // TR Vmax is in m/s, RAP is in mmHg
  const trVmax = parseNumericValue(measurements['TR_Vmax']);
  const rap = parseNumericValue(measurements['RAP']);
  if (trVmax && rap) {
    const rvsp = 4 * Math.pow(trVmax, 2) + rap;
    derived['RVSP'] = `${rvsp.toFixed(0)} mmHg`;
  }

  return derived;
}

export function convertMeasurementsToHTML(
  container,
  fallbackHeightM: number | null = null,
  fallbackWeightKg: number | null = null,
  options: MeasurementResolutionOptions = {}
) {
  if (!container || !container.contentItems) {
    return '';
  }

  const { contentItems } = container;

  // BSA for derived calculations — SR value if present, else DuBois from
  // height/weight in the SR, else from the study-level demographics. Same
  // resolver feeds the demographics header so the two never disagree.
  const bsa = resolveReportBSA(contentItems, fallbackHeightM, fallbackWeightKg);

  // Extract findings based on "Finding Site" and measurements
  const findings = extractFindings(contentItems);

  const filteredResults = filterMeasurements(requiredMeasurementsJSON, findings, options);
  const extraMeasurements = transformCardiacData(findings);


  // Compute derived fields (LVMI, LVEDVI, LVESVI, RVSP, DPI)
  const derived = computeDerivedFields(filteredResults, bsa, findings);

  // Merge: filteredResults → extraMeasurements → derived (derived wins if already set by vendor)
  const allMeasurements = { ...filteredResults, ...extraMeasurements };

  // Derived fields that must always override extraMeasurements
  // (extraMeasurements picks up wrong values like A-L index instead of biplane)
  const DERIVED_ALWAYS_OVERRIDE = new Set(['LA_Vol_Ind']);

  for (const [key, value] of Object.entries(derived)) {
    if (DERIVED_ALWAYS_OVERRIDE.has(key) || !allMeasurements[key]) {
      allMeasurements[key] = value;
    }
  }

  // Remove internal raw fields (used for derivation) from final output
  delete allMeasurements['LVEDVRAW'];
  delete allMeasurements['LVESVRAW'];
  delete allMeasurements['LVMASSRAW'];
  delete allMeasurements['LAVOLRAW'];

  // Template key aliases — some clinic templates use different placeholders
   if (allMeasurements['E_lateral']) allMeasurements['E_lat'] = allMeasurements['E_lateral'];

  // Overwrite conflicting extraMeasurements keys with derived biplane values
  // transformCardiacData generates keys like "LA2MLeftAtrialEndSystolicVolumeIndex"
  // which use A-L index — replace with our derived biplane value
  if (derived['LA_Vol_Ind']) {
    for (const key of Object.keys(allMeasurements)) {
      if (/VolumeIndex|VolInd/i.test(key) && /Atri/i.test(key)) {
        allMeasurements[key] = derived['LA_Vol_Ind'];
      }
    }
  }

  // Remove any leaked non-measurement values (e.g. "Doppler Pulsed", "2D mode")
  // A valid measurement must contain at least one digit
  for (const key of Object.keys(allMeasurements)) {
    const val = allMeasurements[key];
    if (typeof val === 'string' && val.length > 0 && !/\d/.test(val)) {
      delete allMeasurements[key];
    }
  }

  return allMeasurements;
}

export function getSeriesMetadata(StudyInstanceUID, SeriesInstanceUID) {
  // Retrieve the series metadata using StudyInstanceUID and SeriesInstanceUID
  const seriesMetadata = DicomMetadataStore.getSeries(StudyInstanceUID, SeriesInstanceUID);
  if (!seriesMetadata) {
    console.warn(
      `No series metadata found for StudyInstanceUID: ${StudyInstanceUID} and SeriesInstanceUID: ${SeriesInstanceUID}`
    );
    return null;
  }
  return seriesMetadata;
}

// Helper: names that indicate a "Calculation" section (key-value pairs, not arrays)
const CALCULATION_NAMES = new Set([
  'User-defined concepts',
  'Calculation',
  'Calculations',
  'Derived Measurements',
  'Calculated Values',
]);

// Helper function to extract findings and group them by "Finding Site"
// Walks the ENTIRE SR tree recursively — not limited to "Findings" container
// Each measurement is tagged with a unique `mgIndex` so downstream lookups
// (e.g. Measurement Method sibling scan) can stay within the same Measurement
// Group container instead of leaking into adjacent measurements.
function extractFindings(contentItems) {
  if (!Array.isArray(contentItems)) {
    return {};
  }
  const findings = {};
  let mgCounter = 0;

  // Ensure a site key exists as an array
  function ensureSiteArray(site) {
    if (site && !findings[site]) {
      findings[site] = [];
    }
  }

  // Recursive walker — traverses every container at any depth
  // currentSite tracks the nearest Finding Site ancestor
  function walkItems(items, currentSite) {
    if (!Array.isArray(items)) return;

    items.forEach(item => {
      const { codeMeaning, contentItems: nestedItems } = item;
      // --- Calculation / User-defined concepts (stored as object, not array) ---
      if (CALCULATION_NAMES.has(codeMeaning) && Array.isArray(nestedItems)) {
        if (!findings['Calculation'] || Array.isArray(findings['Calculation'])) {
          findings['Calculation'] = {};
        }
        nestedItems.forEach(calc => {
          const key = calc?.contentItems?.[0]?.value;
          const value = calc?.contentItems?.[1]?.value;
          if (key && value) {
            findings['Calculation'][key] = value;
          }
        });
        return;
      }

      // --- Finding Site marker — update current site context ---
      if (codeMeaning === 'Finding Site') {
        currentSite = item.value;
        ensureSiteArray(currentSite);
      }

      // --- Measurement Group — extract measurements under current site ---
      if (codeMeaning === 'Measurement Group' && nestedItems) {
        // Check if this group has its own Finding Site inside it
        const innerSite = nestedItems.find(n => n.codeMeaning === 'Finding Site');
        const siteForGroup = innerSite ? innerSite.value : currentSite;
        ensureSiteArray(siteForGroup);

        const groupId = mgCounter++;
        const measurements = extractMeasurements(nestedItems, groupId);
        if (siteForGroup && measurements.length > 0) {
          findings[siteForGroup].push(...measurements);
        }
        return; // measurements already extracted, don't recurse into this group again
      }

      // --- Any other container — recurse deeper ---
      if (Array.isArray(nestedItems) && nestedItems.length > 0) {
        walkItems(nestedItems, currentSite);
      }
    });
  }

  walkItems(contentItems, '');
  return findings;
}

// Helper function to extract unique key-value pairs from Measurement Groups.
// Each item is tagged with `mgIndex` so the caller can correlate a measurement
// with its concept modifiers (Measurement Method, Image View, etc.).
// Walks nested contentItems so concept modifiers attached to a NUM/CODE leaf
// (e.g. Measurement Method under an EF NUM) are captured as siblings.
const nestedValue = (nested, codeMeaning: string) =>
  Array.isArray(nested) ? nested.find(n => n?.codeMeaning === codeMeaning)?.value : undefined;

function extractMeasurements(contentItems, mgIndex) {
  const uniqueMeasurements = new Map();
  if (!Array.isArray(contentItems)) {
    return [];
  }
  const visit = (item) => {
    if (!item) return;
    const { codeMeaning, codeValue, value, contentItems: nested } = item;
    if (codeMeaning && value) {
      const key = `${codeMeaning} (${codeValue})`;
      const uniqueKey = `${key}:${value}`; // Use raw value to avoid rounding-based false dedup
      if (!uniqueMeasurements.has(uniqueKey)) {
        // Capture this item's OWN Measurement Method (DCM G-C036), if the SR nests
        // it as a direct child of this measurement — the true DICOM SR relationship.
        // Attaching it here (vs. inferring it later by flat-array proximity) keeps
        // the pairing correct even when other measurements interleave in the
        // flattened list. Falls back to proximity scanning in findMatchingValues
        // when a vendor doesn't nest it this way.
        const directMethod = Array.isArray(nested)
          ? nested.find(n => n?.codeMeaning === 'Measurement Method')?.value
          : undefined;
        uniqueMeasurements.set(uniqueKey, {
          key,
          value,
          mgIndex,
          measurementMethod: directMethod,
          // Philips/GE nest the precise site (e.g. Lateral Mitral Annulus, LVOT) under the NUM.
          findingSite: nestedValue(nested, 'Finding Site'),
          derivation: nestedValue(nested, 'Derivation'),
        });
      } else if (!uniqueMeasurements.get(uniqueKey).derivation) {
        // Same value sent again as the vendor's Mean copy.
        uniqueMeasurements.get(uniqueKey).derivation = nestedValue(nested, 'Derivation');
      }
    }
    if (Array.isArray(nested) && nested.length > 0) {
      nested.forEach(visit);
    }
  };
  contentItems.forEach(visit);
  // Convert the unique measurements back to an array
  return Array.from(uniqueMeasurements?.values());
}

// Helper function to render measurements in key-value format
// function renderMeasurements(measurements) {
//   if (!measurements || measurements.length === 0) {
//     return '<p>[No measurements available]</p>';
//   }

//   let html = '';
//   measurements?.forEach(({ key, value }) => {
//     if (!key.includes('Image Mode')) {
//       html += `<strong>${formatedLabel(key)}:</strong><span> ${formatValue(value)}<span/><br>`;
//     }
//   });

//   return html;
// }

export const formatedLabel = key => {
  const result = key
    .replace(/\s*\(.*$/, '') // Remove everything after the '('
    .split(' ') // Split the string into words
    .map(word => word.charAt(0)) // Extract the first letter of each word
    .join(''); // Join the first letters together

  return result;
};

const formatValue = valueWithUnit => {
  if (!valueWithUnit) {
    return '';
  }
  // Match the value and the unit using a regular expression
  const match = valueWithUnit.match(/^(\d+(\.\d+)?)\s*(.*)$/);

  if (match) {
    // Format the numeric part to 2 decimal places
    const value = parseFloat(match[1]).toFixed(2);
    const unit = match[3].trim(); // Capture the full unit, including any special characters

    // Return the formatted value with the unit intact
    return `${value} ${unit}`;
  }

  // If no match, return the original string
  return valueWithUnit;
};

const formatDate = date => {
  const day = String(date.getDate()).padStart(2, '0'); // Get day with leading zero
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Get month (0-indexed, so +1) with leading zero
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Walk a DICOM SR container into the flat `{ codeMeaning, value, contentItems }` shape
 * the report extractors consume.
 *
 * Lifted out of ReportsLayout so the measurement workspace can read the same SR the
 * report reads. Two surfaces parsing an SR two different ways is how a printed report
 * ends up disagreeing with the screen it was approved on — the defect class this module
 * has already had to fix once.
 *
 * The two ReportsLayout side effects it used to perform (setting the report name and
 * the SR's Body Surface Area) are now optional callbacks, so a caller that only wants
 * the parsed tree gets exactly that and nothing else.
 */
export function extractContainerData(
  container,
  nodeIndexesTree = [0],
  containerNumberedTree = [1],
  hooks: { onReportName?: (name: string) => void; onBSA?: (bsa: string) => void } = {}
) {
  const { ContinuityOfContent, ConceptNameCodeSequence, ContentSequence } = container ?? {};
  const { CodeMeaning } = ConceptNameCodeSequence ?? {};
  let childContainerIndex = 1;

  // The outermost container names the report.
  if (nodeIndexesTree.length === 1 && containerNumberedTree.length === 1) {
    hooks.onReportName?.(CodeMeaning);
  }

  const result = {
    codeMeaning: CodeMeaning,
    containerNumberedTree: containerNumberedTree.join('.'),
    continuityOfContent: ContinuityOfContent,
    contentItems: [] as any[],
  };

  if (ContentSequence && Array.isArray(ContentSequence)) {
    result.contentItems = ContentSequence.map((contentItem, i) => {
      const { ValueType } = contentItem;
      const childNodeLevel = [...nodeIndexesTree, i];

      if (ValueType === 'CONTAINER') {
        const childContainerNumberedTree = [...containerNumberedTree, childContainerIndex++];
        const childResult = extractContainerData(
          contentItem,
          childNodeLevel,
          childContainerNumberedTree,
          hooks
        );
        // BSA lives nested under "Patient Characteristics" rather than at the top level.
        if (
          contentItem.ConceptNameCodeSequence?.CodeMeaning === 'Patient Characteristics' &&
          contentItem.ContentSequence
        ) {
          contentItem.ContentSequence.forEach(nestedItem => {
            if (
              nestedItem.ValueType === 'NUM' &&
              nestedItem.ConceptNameCodeSequence?.CodeMeaning === 'Body Surface Area'
            ) {
              const bsaValueRaw =
                nestedItem.NumericValue ||
                (nestedItem.MeasuredValueSequence?.[0]?.NumericValue ?? 'N/A');
              const bsaValue =
                bsaValueRaw !== 'N/A' ? `${parseFloat(bsaValueRaw).toFixed(2)} m²` : 'N/A';
              hooks.onBSA?.(bsaValue);
            }
          });
        }

        return childResult;
      }

      const flat: any = extractContentItemData(contentItem, childNodeLevel, ContinuityOfContent);
      // Preserve nested concept modifiers (e.g. Measurement Method under a NUM) so
      // downstream extraction can read them as siblings of the measurement they qualify.
      if (
        contentItem &&
        Array.isArray(contentItem.ContentSequence) &&
        contentItem.ContentSequence.length > 0
      ) {
        flat.contentItems = contentItem.ContentSequence.map((nested, ni) => {
          const nestedNodeLevel = [...childNodeLevel, ni];
          if (nested.ValueType === 'CONTAINER') {
            const nestedContainerTree = [...containerNumberedTree, childContainerIndex++];
            return extractContainerData(nested, nestedNodeLevel, nestedContainerTree, hooks);
          }
          return extractContentItemData(nested, nestedNodeLevel, ContinuityOfContent);
        });
      }
      return flat;
    });
  }

  return result;
}

/**
 * Heart rate from the Structured Report series linked to the report.
 *
 * Heart rate is not a demographic DICOM tag — it lives in SR measurement content — so
 * this reads only the exact report-linked SR series rather than choosing an arbitrary SR
 * from the study. Returns a number in bpm, or null when that series is unavailable or
 * has no heart rate.
 *
 * Deliberately the SAME parsing path the report uses (extractContainerData →
 * extractHeartRateFromContainer). A missing link is withheld; it is never guessed.
 */
export function getStudyHeartRateBpm(
  StudyInstanceUID: string | null | undefined,
  SeriesInstanceUID: string | null | undefined,
  metadataStore: { getSeries?: (studyUID: string, seriesUID: string) => any } = DicomMetadataStore
): number | null {
  if (!StudyInstanceUID || !SeriesInstanceUID) return null;
  try {
    const series = metadataStore?.getSeries?.(StudyInstanceUID, SeriesInstanceUID);
    const instance = series?.instances?.[0];
    if (!instance || String(instance.Modality || series?.Modality || '') !== 'SR') {
      return null;
    }
    const container = extractContainerData(instance);
    const display = extractHeartRateFromContainer(container?.contentItems);
    if (display) {
      const n = parseFloat(display);
      if (!isNaN(n)) return n;
    }
  } catch {
    /* unavailable/malformed report-linked SR — the row simply stays empty */
  }
  return null;
}
